// Result banner for the "Save Quote" action on the quote detail screen.
//
// Only ever reports what actually happened. The success state renders the
// quote id the backend returned — it is never shown without one, because
// the id is the only proof a real Odoo record exists. A failed or timed-out
// save shows the reason, never a reassuring message.

import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { formatRupees } from "@/lib/format";
import {
  semanticColors, radius, spacing, typography, iconSize, iconStroke,
} from "@/styles/tokens";

export type SaveStatus = "idle" | "saving" | "saved" | "error";

const ROW = {
  display: "flex",
  alignItems: "flex-start",
  gap: spacing[8],
  marginTop: spacing[12],
  padding: `${spacing[8]}px ${spacing[12]}px`,
  borderRadius: radius.base,
  fontSize: typography.fontSize.sm,
} as const;

export function SaveQuoteBanner({
  status,
  quoteId,
  quoteName,
  grandTotal,
  error,
}: {
  status: SaveStatus;
  quoteId?: number;
  // The reference Odoo assigned. Shown because for a new quote it is not
  // the number the user saw before pressing Save.
  quoteName?: string;
  grandTotal?: number;
  error: string;
}) {
  if (status === "idle") return null;

  if (status === "saving") {
    return (
      <div
        data-testid="save-quote-saving"
        style={{
          ...ROW,
          backgroundColor: semanticColors.panelBg,
          border: `1px solid ${semanticColors.border}`,
          color: semanticColors.textSecondary,
        }}
      >
        <Loader2
          size={iconSize.md}
          strokeWidth={iconStroke.regular}
          className="animate-spin"
          style={{ flexShrink: 0, marginTop: spacing[2] }}
        />
        <span>Saving to Odoo…</span>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div
        data-testid="save-quote-error"
        style={{
          ...ROW,
          backgroundColor: semanticColors.destructiveBg,
          border: `1px solid ${semanticColors.destructiveBorder}`,
          color: semanticColors.destructive,
        }}
      >
        <AlertCircle
          size={iconSize.md}
          strokeWidth={iconStroke.regular}
          style={{ flexShrink: 0, marginTop: spacing[2] }}
        />
        <span>
          <strong>Not saved.</strong> {error}
        </span>
      </div>
    );
  }

  // status === "saved" — only reachable with a real id from the backend.
  return (
    <div
      data-testid="save-quote-success"
      style={{
        ...ROW,
        backgroundColor: semanticColors.successBg,
        border: `1px solid ${semanticColors.successBorder}`,
        color: semanticColors.success,
      }}
    >
      <CheckCircle2
        size={iconSize.md}
        strokeWidth={iconStroke.regular}
        style={{ flexShrink: 0, marginTop: spacing[2] }}
      />
      {/* Explicit string literals rather than JSX {" "} separators: the
          latter collapse against the adjacent inline <strong> and render as
          "Quote#42". */}
      <span className="tabular-nums">
        {"Saved to Odoo — Quote "}
        <strong data-testid="save-quote-id">#{quoteId}</strong>
        {quoteName && (
          <>
            {", reference "}
            <strong data-testid="save-quote-name">{quoteName}</strong>
          </>
        )}
        {grandTotal !== undefined && (
          <>
            {", total "}
            <strong data-testid="save-quote-total">{formatRupees(grandTotal)}</strong>
          </>
        )}
        .
      </span>
    </div>
  );
}
